import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faHardHat, faCalendarAlt, faPoundSign
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/ConstructionRisk-Process.png";
import vis from "../../assets/images/use-cases/ConstructionRisk-Dashboard.png";

const ConstructionProjectRisk = () => {
    return (
        <>
            <SEO
                title="Construction Project Risk: AI Digital Twin for Construction | Nebulyx AI"
                description="Model your construction portfolio as connected schedules, budgets, and risk factors. Simulate delays, change orders, and resource constraints to predict overruns and deliver projects on time."
                keywords="AI digital twin, construction risk, project management, schedule delay, cost overrun, earned value, critical path"
                canonicalUrl="https://nebulyx.ai/use-cases/construction-project-risk"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faHardHat}/>
                                            &nbsp;
                                            Construction
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;Which projects will overrun budget or schedule?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Model your construction portfolio as connected schedules, budgets, and risk factors. Simulate delays, change orders, and resource constraints offline, before overruns become inevitable.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faCalendarAlt} className="stat-icon"/>
                                <span className="stat-value">-35%</span>
                                <span className="stat-label">Schedule Overruns</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faPoundSign} className="stat-icon"/>
                                <span className="stat-value">-28%</span>
                                <span className="stat-label">Cost Overruns</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">£4.2M</span>
                                <span className="stat-label">Penalties Avoided</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Early warning signals buried in silos</li>
                                            <li>Problems visible only when too late</li>
                                            <li>No visibility into cascade effects</li>
                                            <li>Reactive firefighting, not prevention</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Build a digital twin of your portfolio</li>
                                            <li>Model schedules, costs, and dependencies</li>
                                            <li>Overlay predictive risk scoring</li>
                                            <li>Simulate interventions before committing</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Predict overruns weeks in advance</li>
                                            <li>Quantify cost/schedule trade-offs</li>
                                            <li>Prioritise interventions by impact</li>
                                            <li>Avoid liquidated damages</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connect project management, financial, and site systems. Model schedules and dependencies. Overlay risk prediction. Simulate scenarios to test interventions before committing resources.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Data</strong>
                                    <span>Primavera, ERP, procurement, site logs, BIM</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Model Dependencies</strong>
                                    <span>Critical path, float, resource links, cost mapping</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Overlay Analytics</strong>
                                    <span>SPI/CPI, EAC, risk scoring, leading indicators</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Scenario Analysis</strong>
                                    <span>Simulate delays, acceleration, change orders</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests project schedules with task dependencies, links to financial actuals for earned value analysis,
                                            and extracts leading indicators from RFI velocity, change orders, and subcontractor performance.
                                            ML models predict completion dates and Estimate at Completion based on current trajectory and historical patterns.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Project Command Centre</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Visualises portfolio health with projects ranked by risk score, showing SPI/CPI performance at a glance.
                                            Drill into any project to see Gantt-style schedules with critical path highlighted, active risk factors,
                                            and forecast outcomes. Simulate schedule crashing, weather delays, or change orders to see impact before deciding.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Project Schedules (Primavera)</li>
                                            <li>Financial Actuals & Commitments</li>
                                            <li>Change Order Log</li>
                                            <li>RFI & Submittal Tracker</li>
                                            <li>Daily Site Logs</li>
                                            <li>Weather Data</li>
                                            <li>Subcontractor Performance</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Schedule Performance Index (SPI)</li>
                                            <li>Cost Performance Index (CPI)</li>
                                            <li>Estimate at Completion (EAC)</li>
                                            <li>Risk-adjusted finish forecast</li>
                                            <li>Critical path float analysis</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Crash schedule (+crew)</li>
                                            <li>Weather delay (+2 weeks)</li>
                                            <li>Major change order</li>
                                            <li>Subcontractor default</li>
                                            <li>Material delivery delay</li>
                                            <li>Scope reduction options</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Potential Discovery</h3>
                                    <p>
                                        By modelling construction projects as a digital twin and tracking leading indicators,
                                        project teams could discover that facade installation is on the critical path and 12 days behind, with
                                        3 subcontractors contributing to the delay before it shows up in monthly reports.
                                        Simulation may reveal that <strong>crashing the schedule recovers 12 days but costs £1.2M, only worthwhile
                                        if liquidated damages exceed £100k/day</strong>. Alternatively, re-sequencing indoor work during
                                        weather delays maintains progress at zero additional cost-all tested before committing resources.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Potential Strategy</span>
                                        <span className="discovery-value">Resequence → Save 12 Days + £1.2M</span>
                                        <span className="discovery-note">Zero-cost alternative to crashing</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Ready to Predict Project Risk Before It Hits?</h2>
                            <p>
                                If schedule delays and cost overruns are eroding margins,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default ConstructionProjectRisk;